/**
 * Planet vertex colours — ONE definition (computePlanetVertexColors) used by the
 * renderer at creation and by crater/mound updates, so the surface never changes
 * look after an impact.
 *
 * Shipped colouring = surface material layers (PLANET_LAYERS, see
 * computeLayeredVertexColors): each facet classified by height into a named band
 * with noise-broken edges, steep facets to rock, a subtle per-facet tint, and an
 * optional impact overlay (crater scorch / mound tint). The older 3-band gradient
 * (computeGradientVertexColors) is kept for the design sheet's reference row.
 *
 * The mesh is flat-shaded and non-indexed, so slope and tint are computed per
 * triangle and applied to its three vertices.
 */

import type { PlanetState, Vec3, PlanetLayerProfile, PlanetLayer } from '../types';
import { PLANET_LAYERS, PLANET_STRATA } from '../constants';
import { positionJitter } from './mapgen';
import { dacos, dcos, dsin } from './dmath';
import { verticesNear } from './vertexCells';
import { AFTER_HIT } from '../constants/groundLook';
import { GROUND_MATERIALS } from '../constants/ground';
import type { GroundMaterial } from '../types/ground';

export interface RGB { r: number; g: number; b: number }

export function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(result[1], 16) / 255,
    g: parseInt(result[2], 16) / 255,
    b: parseInt(result[3], 16) / 255,
  };
}

export interface TerrainOverlay {
  impactDir: Vec3;        // unit direction (planet-local) of the impact
  angularRadius: number;  // radians
  kind: 'crater' | 'mound';
}

/** Build the overlay for an impact at a world point. */
export function impactOverlay(planet: PlanetState, impactWorld: Vec3, blastRadius: number, kind: 'crater' | 'mound'): TerrainOverlay {
  const dx = impactWorld.x - planet.position.x;
  const dy = impactWorld.y - planet.position.y;
  const dz = impactWorld.z - planet.position.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
  return { impactDir: { x: dx / len, y: dy / len, z: dz / len }, angularRadius: blastRadius / planet.radius, kind };
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/**
 * Per-vertex slope factor: 0 = face is flat (normal radial), 1 = vertical.
 * Per triangle, replicated to its three vertices. Non-mesh data → all zeros.
 */
export function computeSlopeFactors(planet: PlanetState): Float32Array {
  const n = planet.heightmap.length;
  const out = new Float32Array(n);
  if (n % 3 !== 0) return out;
  const bp = planet.basePositions;
  const hm = planet.heightmap;
  const p = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let t = 0; t < n / 3; t++) {
    let cx = 0, cy = 0, cz = 0;
    for (let k = 0; k < 3; k++) {
      const vi = t * 3 + k;
      const bx = bp[vi * 3], by = bp[vi * 3 + 1], bz = bp[vi * 3 + 2];
      const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
      const s = 1 + hm[vi] / len;
      p[k * 3] = bx * s; p[k * 3 + 1] = by * s; p[k * 3 + 2] = bz * s;
      cx += p[k * 3]; cy += p[k * 3 + 1]; cz += p[k * 3 + 2];
    }
    const e1x = p[3] - p[0], e1y = p[4] - p[1], e1z = p[5] - p[2];
    const e2x = p[6] - p[0], e2y = p[7] - p[1], e2z = p[8] - p[2];
    let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    nx /= nl; ny /= nl; nz /= nl;
    const cl = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
    const d = Math.abs((nx * cx + ny * cy + nz * cz) / cl);
    const slope = 1 - Math.min(1, d);
    out[t * 3] = slope; out[t * 3 + 1] = slope; out[t * 3 + 2] = slope;
  }
  return out;
}

/** Full vertex colour array (3 floats per vertex) for a planet's current heightmap — the shipped colouring. */
export function computePlanetVertexColors(planet: PlanetState, overlay?: TerrainOverlay): Float32Array {
  return computeLayeredVertexColors(planet, overlay);
}

/** The original 3-band gradient colouring, kept for the design sheet's reference row. */
export function computeGradientVertexColors(planet: PlanetState, overlay?: TerrainOverlay): Float32Array {
  const n = planet.heightmap.length;
  const colors = new Float32Array(n * 3);
  const hm = planet.heightmap;
  const bp = planet.basePositions;

  const colorA = hexToRgb(planet.palette.a);
  const colorB = hexToRgb(planet.palette.b);
  const colorC = planet.palette.c ? hexToRgb(planet.palette.c) : { r: colorA.r * 0.4, g: colorA.g * 0.4, b: colorA.b * 0.4 };
  // Rock tone for steep faces: palette's deep colour pulled toward neutral grey
  const rock = { r: colorC.r * 0.55 + 0.20, g: colorC.g * 0.55 + 0.19, b: colorC.b * 0.55 + 0.18 };

  let minH = Infinity, maxH = -Infinity;
  for (let i = 0; i < n; i++) {
    if (hm[i] < minH) minH = hm[i];
    if (hm[i] > maxH) maxH = hm[i];
  }
  const hRange = maxH - minH || 1;

  const slopes = computeSlopeFactors(planet);
  const isMesh = n % 3 === 0;

  for (let i = 0; i < n; i++) {
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const nx = bx / len, ny = by / len, nz = bz / len;

    // 3-band height gradient
    const normalized = ((hm[i] - minH) / hRange) * 2 - 1;
    let r: number, g: number, b: number;
    if (normalized < 0) {
      const t = normalized + 1;
      r = colorC.r + (colorA.r - colorC.r) * t;
      g = colorC.g + (colorA.g - colorC.g) * t;
      b = colorC.b + (colorA.b - colorC.b) * t;
    } else {
      r = colorA.r + (colorB.r - colorA.r) * normalized;
      g = colorA.g + (colorB.g - colorA.g) * normalized;
      b = colorA.b + (colorB.b - colorA.b) * normalized;
    }

    // Slope shading toward rock on steep faces
    const rockMix = smoothstep(0.15, 0.5, slopes[i]) * 0.7;
    r += (rock.r - r) * rockMix;
    g += (rock.g - g) * rockMix;
    b += (rock.b - b) * rockMix;

    // Per-face tint (same value for the triangle's 3 vertices: hash the first vertex)
    if (isMesh) {
      const v0 = i - (i % 3);
      const tl = Math.sqrt((bp[v0 * 3] * bp[v0 * 3]) + (bp[v0 * 3 + 1] * bp[v0 * 3 + 1]) + (bp[v0 * 3 + 2] * bp[v0 * 3 + 2])) || 1;
      const tint = 1 + positionJitter(bp[v0 * 3] / tl, bp[v0 * 3 + 1] / tl, bp[v0 * 3 + 2] / tl) * 0.05;
      r *= tint; g *= tint; b *= tint;
    }

    // Impact overlay
    if (overlay) {
      const dot = nx * overlay.impactDir.x + ny * overlay.impactDir.y + nz * overlay.impactDir.z;
      const angle = dacos(Math.min(1, Math.max(-1, dot)));
      if (angle < overlay.angularRadius) {
        const blendT = 1 - angle / overlay.angularRadius; // 1 at centre
        if (overlay.kind === 'crater') {
          r = r * (1 - blendT * 0.6) + colorC.r * blendT * 0.6;
          g = g * (1 - blendT * 0.6) + colorC.g * blendT * 0.6;
          b = b * (1 - blendT * 0.6) + colorC.b * blendT * 0.6;
        } else {
          r = r * (1 - blendT * 0.4) + colorB.r * blendT * 0.4;
          g = g * (1 - blendT * 0.4) + colorB.g * blendT * 0.4;
          b = b * (1 - blendT * 0.4) + colorB.b * blendT * 0.4;
        }
      }
    }

    colors[i * 3] = Math.min(1, r);
    colors[i * 3 + 1] = Math.min(1, g);
    colors[i * 3 + 2] = Math.min(1, b);
  }
  return colors;
}

// =============================================================================
// LAYERED COLOURING — surface materials by height band (PLANET_LAYERS)
// =============================================================================

/** Deterministic 3D value noise in [-1, 1] built on positionJitter at lattice corners. */
function valueNoise(x: number, y: number, z: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  const c = (i: number, j: number, k: number) => positionJitter((x0 + i) * 0.37, (y0 + j) * 0.37, (z0 + k) * 0.37);
  const lerp = (a: number, b: number, s: number) => a + (b - a) * s;
  return lerp(
    lerp(lerp(c(0, 0, 0), c(1, 0, 0), sx), lerp(c(0, 1, 0), c(1, 1, 0), sx), sy),
    lerp(lerp(c(0, 0, 1), c(1, 0, 1), sx), lerp(c(0, 1, 1), c(1, 1, 1), sx), sy),
    sz,
  );
}

export interface LayerColorOptions {
  /** Hard band edges (one material per facet) vs soft blends across band edges. */
  hard?: boolean;
  /** Multiplier on the profile's noise break-up (1 = as authored, 0 = clean contour bands). */
  breakup?: number;
  /** Width of the soft blend across a band edge, in normalized height. */
  blend?: number;
}

/** Layer profile for a planet: by archetype, else by palette name (megaplanet earth), else thalia. */
export function layerProfileFor(planet: PlanetState): PlanetLayerProfile {
  const byName = (PLANET_LAYERS as Record<string, PlanetLayerProfile>)[planet.palette.name];
  return (planet.palette.name === 'earth' && byName) || PLANET_LAYERS[planet.archetype] || byName || PLANET_LAYERS.thalia;
}
/** Sub-surface strata for a planet, same selection rule as the layer profile. */
export function strataFor(planet: PlanetState): PlanetLayer[] {
  const byName = (PLANET_STRATA as Record<string, PlanetLayer[]>)[planet.palette.name];
  return (planet.palette.name === 'earth' && byName) || PLANET_STRATA[planet.archetype] || byName || PLANET_STRATA.thalia;
}

/** Dig deeper than this fraction of the radius below the generated surface and the strata show. */
export const STRATA_EPS_FRACTION = 0.004;

/** Generation-time height range, cached per heightmap array: the bands are fixed against it. */
const rangeCache = new WeakMap<Float32Array, [number, number]>();
function heightRange(h0: Float32Array): [number, number] {
  let r = rangeCache.get(h0);
  if (!r) {
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < h0.length; i++) { if (h0[i] < lo) lo = h0[i]; if (h0[i] > hi) hi = h0[i]; }
    r = [lo, hi - lo || 1];
    rangeCache.set(h0, r);
  }
  return r;
}

/** Slope factor of one facet from the current heightmap (0 = flat facing out, 1 = vertical). */
function facetSlope(bp: Float32Array, hm: Float32Array, t: number): number {
  let cx = 0, cy = 0, cz = 0;
  const p = _facetP;
  for (let k = 0; k < 3; k++) {
    const vi = t * 3 + k;
    const bx = bp[vi * 3], by = bp[vi * 3 + 1], bz = bp[vi * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const s = 1 + hm[vi] / len;
    p[k * 3] = bx * s; p[k * 3 + 1] = by * s; p[k * 3 + 2] = bz * s;
    cx += p[k * 3]; cy += p[k * 3 + 1]; cz += p[k * 3 + 2];
  }
  const e1x = p[3] - p[0], e1y = p[4] - p[1], e1z = p[5] - p[2];
  const e2x = p[6] - p[0], e2y = p[7] - p[1], e2z = p[8] - p[2];
  let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  nx /= nl; ny /= nl; nz /= nl;
  const cl = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
  return 1 - Math.min(1, Math.abs((nx * cx + ny * cy + nz * cz) / cl));
}
const _facetP = [0, 0, 0, 0, 0, 0, 0, 0, 0];

export interface LayerColorOptions {
  /** Hard band edges (one material per facet) vs soft blends across band edges. */
  hard?: boolean;
  /** Multiplier on the profile's noise break-up (1 = as authored, 0 = clean contour bands). */
  breakup?: number;
  /** Width of the soft blend across a band edge, in normalized height. */
  blend?: number;
  /** Recolour only these vertices (their whole facets), writing into `into`. */
  only?: ArrayLike<number>;
  /** Existing colour array to update in place (required with `only`). */
  into?: Float32Array;
}

/**
 * Vertex colours as a pure function of the planet's materials:
 *   - the surface band is classified against the heightmap *as generated*
 *     (`initialHeightmap`), normalized by the generation-time range, so a crater
 *     anywhere never re-maps the bands elsewhere;
 *   - digging below the generated surface exposes the archetype's strata by
 *     depth (topsoil → bedrock → deep), which persists like real material;
 *   - raised terrain (terraformer mounds) takes the surface band of its new height;
 *   - steep facets turn to rock, every facet keeps its ±5% tint;
 *   - `planet.scorch` (persistent impact tint, see accumulateScorch) is applied,
 *     then any transient `overlay`.
 * Deterministic. With `only`/`into` it recolours just the touched facets.
 */
export function computeLayeredVertexColors(planet: PlanetState, overlay?: TerrainOverlay, opts: LayerColorOptions = {}): Float32Array {
  const profile = layerProfileFor(planet);
  const strata = strataFor(planet);
  const hard = opts.hard ?? true;
  const breakup = profile.breakup * (opts.breakup ?? 1);
  const blend = opts.blend ?? 0.04;
  const layers = profile.layers.map(l => ({ upTo: l.upTo, rgb: hexToRgb(l.color) }));
  const strataRgb = strata.map(l => ({ upTo: l.upTo, rgb: hexToRgb(l.color) }));
  const rock = hexToRgb(profile.rock);
  const craterRgb = hexToRgb(profile.crater);
  const moundRgb = hexToRgb(profile.mound);
  const hm = planet.heightmap;
  const h0 = planet.initialHeightmap ?? hm;
  const n = hm.length;
  const colors = opts.into ?? new Float32Array(n * 3);
  const bp = planet.basePositions;
  const [minH, hRange] = heightRange(h0);
  const isMesh = n % 3 === 0;
  const scorch = planet.scorch;
  const look = planet.hitLook;
  const NOISE_FREQ = 3.2;
  const eps = planet.radius * STRATA_EPS_FRACTION;

  const pickBand = (bands: Array<{ upTo: number; rgb: RGB }>, h: number): RGB => {
    let idx = bands.length - 1;
    for (let k = 0; k < bands.length; k++) { if (h <= bands[k].upTo) { idx = k; break; } }
    const cur = bands[idx].rgb;
    if (hard) return cur;
    const lo = idx > 0 ? bands[idx - 1].upTo : -Infinity;
    const hi = bands[idx].upTo;
    if (idx > 0 && h - lo < blend) {
      const t = 0.5 + 0.5 * ((h - lo) / blend);
      const prev = bands[idx - 1].rgb;
      return { r: prev.r + (cur.r - prev.r) * t, g: prev.g + (cur.g - prev.g) * t, b: prev.b + (cur.b - prev.b) * t };
    }
    if (idx < bands.length - 1 && hi - h < blend) {
      const t = 0.5 * (1 - (hi - h) / blend);
      const next = bands[idx + 1].rgb;
      return { r: cur.r + (next.r - cur.r) * t, g: cur.g + (next.g - cur.g) * t, b: cur.b + (next.b - cur.b) * t };
    }
    return cur;
  };

  // Which vertices to colour: all, or the facets touching `only`
  let targets: ArrayLike<number> | null = null;
  if (opts.only) {
    if (!opts.into) throw new Error('computeLayeredVertexColors: `only` requires `into`');
    const set = new Set<number>();
    for (let k = 0; k < opts.only.length; k++) {
      const i = opts.only[k];
      if (isMesh) { const f = i - (i % 3); set.add(f); set.add(f + 1); set.add(f + 2); } else set.add(i);
    }
    targets = Array.from(set);
  }
  const count = targets ? targets.length : n;
  const slopeCache = new Map<number, number>();

  for (let k = 0; k < count; k++) {
    const i = targets ? targets[k] : k;
    const v0 = isMesh ? i - (i % 3) : i;
    const rx = bp[v0 * 3], ry = bp[v0 * 3 + 1], rz = bp[v0 * 3 + 2];
    const rl = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1;
    const dx = rx / rl, dy = ry / rl, dz = rz / rl;
    // heights: per-facet means in hard mode (one material per facet), per-vertex when blending
    let hNow: number, hGen: number;
    if (hard && isMesh) { hNow = (hm[v0] + hm[v0 + 1] + hm[v0 + 2]) / 3; hGen = (h0[v0] + h0[v0 + 1] + h0[v0 + 2]) / 3; }
    else { hNow = hm[i]; hGen = h0[i]; }
    const noise = breakup > 0 ? valueNoise(dx * NOISE_FREQ + 11.3, dy * NOISE_FREQ + 7.1, dz * NOISE_FREQ + 3.9) * breakup : 0;
    let rgb: RGB;
    const dug = hGen - hNow;
    if (dug > eps) {
      // Below the generated surface: exposed strata by depth (fraction of radius), noise meanders the seams
      const d = Math.max(0, Math.min(1, dug / planet.radius + noise * 0.02));
      rgb = pickBand(strataRgb, d);
    } else {
      // At (or above) the generated surface: the surface band of the current height,
      // normalized against the generation-time range so nothing else ever shifts
      const h = Math.max(0, Math.min(1, ((dug < -eps ? hNow : hGen) - minH) / hRange + noise));
      rgb = pickBand(layers, h);
    }
    let { r, g, b } = rgb;
    // steep facets → rock (slope from the current terrain)
    let s: number;
    if (isMesh) { const t = v0 / 3; s = slopeCache.get(t) ?? (() => { const v = facetSlope(bp, hm, t); slopeCache.set(t, v); return v; })(); }
    else s = 0;
    const rockMix = hard ? (s >= (profile.rockSlope[0] + profile.rockSlope[1]) / 2 ? 1 : 0) : smoothstep(profile.rockSlope[0], profile.rockSlope[1], s);
    if (rockMix > 0) { r += (rock.r - r) * rockMix; g += (rock.g - g) * rockMix; b += (rock.b - b) * rockMix; }
    // per-facet tint keeps the low-poly read
    if (isMesh) {
      const tint = 1 + positionJitter(dx, dy, dz) * 0.05;
      r *= tint; g *= tint; b *= tint;
    }
    // 63.5 step 5: a crater's look by the ground it was dug in, in place of the one scorch mix (the switch's)
    const lookAt = look ? look.material[i] : 255;
    if (lookAt !== 255) {
      const o = afterHitRgb(GROUND_MATERIALS[lookAt], r, g, b, look!.t[i]);
      r = o.r; g = o.g; b = o.b;
    } else
    // persistent impact tint (scorch > 0, mound < 0)
    if (scorch) {
      const w = scorch[i];
      if (w > 0) { r += (craterRgb.r - r) * w; g += (craterRgb.g - g) * w; b += (craterRgb.b - b) * w; }
      else if (w < 0) { r += (moundRgb.r - r) * -w; g += (moundRgb.g - g) * -w; b += (moundRgb.b - b) * -w; }
    }
    // transient overlay (design sheet / previews) — same contract as before
    if (overlay) {
      const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
      const bl = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
      const dot = (bx * overlay.impactDir.x + by * overlay.impactDir.y + bz * overlay.impactDir.z) / bl;
      const angle = dacos(Math.min(1, Math.max(-1, dot)));
      if (angle < overlay.angularRadius) {
        const w = (1 - angle / overlay.angularRadius) * (overlay.kind === 'crater' ? 0.7 : 0.4);
        const tgt = overlay.kind === 'crater' ? craterRgb : moundRgb;
        r += (tgt.r - r) * w; g += (tgt.g - g) * w; b += (tgt.b - b) * w;
      }
    }
    colors[i * 3] = Math.min(1, r);
    colors[i * 3 + 1] = Math.min(1, g);
    colors[i * 3 + 2] = Math.min(1, b);
  }
  return colors;
}

/**
 * The vertices `recolorVertices(…, vertices)` may write — each one's whole facet on a mesh (three unshared vertices a
 * facet), each vertex alone otherwise — ascending, each once (the fog's changed vertices are looked for among them).
 */
export function recolouredFacets(vertexCount: number, vertices: readonly number[]): number[] {
  const isMesh = vertexCount % 3 === 0, seen = new Set<number>();
  for (const i of vertices) { if (isMesh) { const f = i - (i % 3); seen.add(f); seen.add(f + 1); seen.add(f + 2); } else seen.add(i); }
  return Array.from(seen).sort((a, b) => a - b);
}

/** Full recolour from scratch — identical to recolouring every vertex. */
export function recolorVertices(planet: PlanetState, prevColors: Float32Array, vertices: ArrayLike<number>): Float32Array {
  return computeLayeredVertexColors(planet, undefined, { only: vertices, into: new Float32Array(prevColors) });
}

// ---- 63.5 step 5: the look after a hit, by the ground's material --------------------------------------------------------

const lookRgb = new Map<string, RGB>();
const rgbOf = (hex: string): RGB => { let c = lookRgb.get(hex); if (!c) { c = hexToRgb(hex); lookRgb.set(hex, c); } return c; };

/**
 * Lane E's after-hit look (constants/groundLook.ts AFTER_HIT; rendering/groundAfterHit.ts's afterHitTint wraps this, so the
 * design sheet and the game draw one thing): the colour at a point `t` of a crater's radius out from its centre (0 the
 * centre, 1 the rim, untouched from 1.4), from the terrain's colour there — the centre mixed toward the material's core,
 * a soft ring toward its ring colour, and the sand's ripples across the bowl.
 */
export function afterHitRgb(material: GroundMaterial, r: number, g: number, b: number, t: number): RGB {
  const L = AFTER_HIT[material];
  if (t >= 1.4) return { r, g, b };
  const centre = Math.max(0, 1 - t / 0.85);
  const cm = L.coreMix * centre * centre * (3 - 2 * centre);
  const c = rgbOf(L.core);
  r += (c.r - r) * cm; g += (c.g - g) * cm; b += (c.b - b) * cm;
  const d = Math.abs(t - L.ringAt) / L.ringWidth;
  if (d < 1) { const m = L.ringMix * (1 - d * d), q = rgbOf(L.ring); r += (q.r - r) * m; g += (q.g - g) * m; b += (q.b - b) * m; }
  if (L.ripples && t < 1.2) { const k = 1 + L.ripples.amount * dsin(t * L.ripples.count * Math.PI * 2); r *= k; g *= k; b *= k; }
  return { r, g, b };
}

/**
 * 63.5 step 5: mark the vertices a crater's look reaches — out to 1.4 of its radius, its throw — with the material it was
 * dug in and how far out each lies (a share of its radius). The new crater takes its own bowl (it redug that ground); in
 * its throw, where an older look lies, the nearer one (the smaller share) stays. Returns the planet with its new look and
 * the vertices marked (to recolour).
 */
export function markGroundLook(planet: PlanetState, impactWorld: Vec3, radius: number, material: GroundMaterial): { planet: PlanetState; marked: number[] } {
  const n = planet.heightmap.length;
  const prev = planet.hitLook;
  const mat = prev ? new Uint8Array(prev.material) : new Uint8Array(n).fill(255);
  const tt = prev ? new Float32Array(prev.t) : new Float32Array(n).fill(Infinity);
  const idx = GROUND_MATERIALS.indexOf(material);
  const ix = impactWorld.x - planet.position.x, iy = impactWorld.y - planet.position.y, iz = impactWorld.z - planet.position.z;
  const il = Math.sqrt(ix * ix + iy * iy + iz * iz) || 1;
  const angR = radius / planet.radius;
  const reach = 1.4 * angR, cosReach = reach >= Math.PI ? -2 : dcos(reach);   // only vertices within the throw pay for the arc
  const bp = planet.basePositions;
  const marked: number[] = [];
  // (the vertices within the throw, ascending — the whole-planet scan's order and arithmetic: core/vertexCells.ts)
  const near = verticesNear(bp, { x: ix / il, y: iy / il, z: iz / il }, reach), count = near ? near.length : n;
  for (let k = 0; k < count; k++) {
    const i = near ? near[k] : k;
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const bl = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const c = (bx * ix + by * iy + bz * iz) / (bl * il);
    if (c < cosReach) continue;
    const t = dacos(Math.min(1, Math.max(-1, c))) / angR;
    if (t >= 1.4 || (t >= 1 && t >= tt[i])) continue;
    mat[i] = idx; tt[i] = t;
    marked.push(i);
  }
  return { planet: { ...planet, hitLook: { material: mat, t: tt } }, marked };
}

/**
 * Persistent impact tint after a blast: crater scorch as a positive weight, a
 * terraformer mound as a negative one; the stronger magnitude wins where they
 * overlap. Returns a new array (immutable state).
 */
export function accumulateScorch(planet: PlanetState, impactWorld: Vec3, blastRadius: number, kind: 'crater' | 'mound'): Float32Array {
  const n = planet.heightmap.length;
  const out = planet.scorch ? new Float32Array(planet.scorch) : new Float32Array(n);
  const ov = impactOverlay(planet, impactWorld, blastRadius, kind);
  const bp = planet.basePositions;
  const strength = kind === 'crater' ? 0.7 : 0.4;
  // 62.56: a vertex whose cosine is clearly under the blast's (a margin past any rounding) is outside it — skipped before the
  // arc, as the arc would skip it; the rest are measured as before (the whole planet's arcs were 0.8 ms a shell)
  const outside = ov.angularRadius < Math.PI ? dcos(ov.angularRadius) - 1e-9 : -2;
  // (the vertices within the blast, ascending — the whole-planet scan's order and arithmetic: core/vertexCells.ts)
  const near = verticesNear(bp, ov.impactDir, ov.angularRadius), count = near ? near.length : n;
  for (let k = 0; k < count; k++) {
    const i = near ? near[k] : k;
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const bl = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const dot = (bx * ov.impactDir.x + by * ov.impactDir.y + bz * ov.impactDir.z) / bl;
    if (dot < outside) continue;
    const angle = dacos(Math.min(1, Math.max(-1, dot)));
    if (angle >= ov.angularRadius) continue;
    const w = (1 - angle / ov.angularRadius) * strength * (kind === 'crater' ? 1 : -1);
    if (Math.abs(w) > Math.abs(out[i])) out[i] = w;
  }
  return out;
}
